// Implementação de ApiClient sobre a API real (api/openapi/v1.yaml).
import { ApiRequestError, type ApiClient } from "../client";
import type * as T from "../types";
import { common } from "../../i18n/common";
import { getLocale, isLocale, setLocale, tNow } from "../../i18n/core";

const BASE = "/api/v1";

let csrfToken = "";

function rememberSession(s: T.Session): T.Session {
  csrfToken = s.csrfToken;
  // A preferência gravada na conta vale em qualquer navegador.
  if (isLocale(s.user.locale)) setLocale(s.user.locale);
  return s;
}

async function request<R>(method: string, path: string, body?: unknown, query?: Record<string, string | undefined>): Promise<R> {
  const url = new URL(BASE + path, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) if (v) url.searchParams.set(k, v);
  // A API traduz mensagens (erros, resultados, auditoria) pelo Accept-Language.
  const headers: Record<string, string> = { Accept: "application/json", "Accept-Language": getLocale() };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;

  let res: Response;
  try {
    res = await fetch(url, { method, headers, credentials: "same-origin", body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiRequestError(0, "network", tNow(common).networkError);
  }
  if (res.status === 204) return undefined as R;
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const err = (data ?? {}) as Partial<T.ApiError>;
    if (res.status === 401) csrfToken = "";
    throw new ApiRequestError(res.status, err.code ?? "http_" + res.status, err.message ?? tNow(common).serverError(res.status));
  }
  return data as R;
}

const org = (id: string) => `/orgs/${encodeURIComponent(id)}`;
const adminOrg = (id: string) => `/admin/orgs/${encodeURIComponent(id)}`;
const pool = (id: string) => `/admin/pools/${encodeURIComponent(id)}`;

export function createHttpClient(): ApiClient {
  return {
    login: (email, password) => request("POST", "/auth/login", { email, password }),
    verifyTotp: async (challengeId, code) => rememberSession(await request<T.Session>("POST", "/auth/totp", { challengeId, code })),
    logout: async () => {
      await request<void>("POST", "/auth/logout");
      csrfToken = "";
    },
    requestPasswordReset: async (email) => {
      await request("POST", "/auth/password-reset", { email });
    },
    confirmPasswordReset: (input) => request("POST", "/auth/password-reset/confirm", input),
    getSession: async () => rememberSession(await request<T.Session>("GET", "/session")),
    getTrialInfo: () => request("GET", "/trial"),
    trialSignup: async (input) => {
      await request("POST", "/trial/signup", input);
    },
    updatePreferences: (input) => request("PUT", "/me/preferences", input),
    changePassword: (input) => request("PUT", "/me/password", input),
    updateProfile: (input) => request("PUT", "/me/profile", input),
    changeEmail: (input) => request("POST", "/me/email", input),
    // O segredo do link vai no corpo, nunca na URL.
    confirmEmailChange: (token) => request("POST", "/me/email/confirm", { token }),

    lookupInvitation: (token) => request("POST", "/invitations/lookup", { token }),
    beginInvitationSignup: (input) => request("POST", "/invitations/signup", input),
    activateInvitation: async (input) => rememberSession(await request<T.Session>("POST", "/invitations/activate", input)),
    joinInvitation: (token) => request("POST", "/invitations/join", { token }),

    listOrganizations: () => request("GET", "/orgs"),
    getOverview: (o) => request("GET", `${org(o)}/overview`),

    listMachines: (o, f) => request("GET", `${org(o)}/machines`, undefined, { status: f?.status, groupId: f?.groupId, q: f?.q }),
    getMachine: (o, m) => request("GET", `${org(o)}/machines/${encodeURIComponent(m)}`),
    startBackup: (o, m) => request("POST", `${org(o)}/machines/${encodeURIComponent(m)}/backups`),
    startImage: (o, m, disks) => request("POST", `${org(o)}/machines/${encodeURIComponent(m)}/images`, { disks }),
    listDisks: (o, m) => request("GET", `${org(o)}/machines/${encodeURIComponent(m)}/disks`),
    listDatabases: (o, m) => request("GET", `${org(o)}/machines/${encodeURIComponent(m)}/databases`),
    foundDatabases: (o, m) => request("GET", `${org(o)}/machines/${encodeURIComponent(m)}/databases/found`),
    createDatabase: (o, m, input) => request("POST", `${org(o)}/machines/${encodeURIComponent(m)}/databases`, input),
    updateDatabase: (o, m, d, input) => request("PUT", `${org(o)}/machines/${encodeURIComponent(m)}/databases/${encodeURIComponent(d)}`, input),
    deleteDatabase: (o, m, d) => request("DELETE", `${org(o)}/machines/${encodeURIComponent(m)}/databases/${encodeURIComponent(d)}`),
    testDatabase: (o, m, d) => request("POST", `${org(o)}/machines/${encodeURIComponent(m)}/databases/${encodeURIComponent(d)}/test`),
    startDatabaseBackup: (o, m, d) => request("POST", `${org(o)}/machines/${encodeURIComponent(m)}/databases/${encodeURIComponent(d)}/backups`),
    listShares: (o, m) => request("GET", `${org(o)}/machines/${encodeURIComponent(m)}/shares`),
    createShare: (o, m, input) => request("POST", `${org(o)}/machines/${encodeURIComponent(m)}/shares`, input),
    updateShare: (o, m, s, input) => request("PUT", `${org(o)}/machines/${encodeURIComponent(m)}/shares/${encodeURIComponent(s)}`, input),
    deleteShare: (o, m, s) => request("DELETE", `${org(o)}/machines/${encodeURIComponent(m)}/shares/${encodeURIComponent(s)}`),
    testShare: (o, m, s) => request("POST", `${org(o)}/machines/${encodeURIComponent(m)}/shares/${encodeURIComponent(s)}/test`),
    startShareBackup: (o, m, s) => request("POST", `${org(o)}/machines/${encodeURIComponent(m)}/shares/${encodeURIComponent(s)}/backups`),
    listCredentials: (o) => request("GET", `${org(o)}/credentials`),
    createCredential: (o, input) => request("POST", `${org(o)}/credentials`, input),
    updateCredential: (o, id, input) => request("PUT", `${org(o)}/credentials/${encodeURIComponent(id)}`, input),
    deleteCredential: (o, id) => request("DELETE", `${org(o)}/credentials/${encodeURIComponent(id)}`),
    revokeMachine: (o, m) => request("POST", `${org(o)}/machines/${encodeURIComponent(m)}/revoke`),
    updateMachine: (o, m, input) => request("PATCH", `${org(o)}/machines/${encodeURIComponent(m)}`, input),
    listSnapshots: (o, m) => request("GET", `${org(o)}/machines/${encodeURIComponent(m)}/snapshots`),
    snapshotErrors: (o, id) => request("GET", `${org(o)}/snapshots/${encodeURIComponent(id)}/errors`),
    browseSnapshot: (o, s, path) => request("GET", `${org(o)}/snapshots/${encodeURIComponent(s)}/files`, undefined, { path }),
    searchCatalog: (o, m, q) => request("GET", `${org(o)}/machines/${encodeURIComponent(m)}/catalog/search`, undefined, { q }),
    catalogOccurrences: (o, m, dir, name) => request("GET", `${org(o)}/machines/${encodeURIComponent(m)}/catalog/occurrences`, undefined, { dir, name }),
    createRestore: (o, req) => request("POST", `${org(o)}/restores`, req),

    listGroups: (o) => request("GET", `${org(o)}/groups`),
    listPolicies: (o) => request("GET", `${org(o)}/policies`),
    createPolicy: (o, input) => request("POST", `${org(o)}/policies`, input),
    updatePolicy: (o, p, input) => request("PUT", `${org(o)}/policies/${encodeURIComponent(p)}`, input),
    getUsage: (o) => request("GET", `${org(o)}/usage`),
    storage: (o) => request("GET", `${org(o)}/storage`),

    adminListPools: () => request("GET", "/admin/pools"),
    adminCreatePool: (input) => request("POST", "/admin/pools", input),
    adminUpdatePool: (p, input) => request("PUT", pool(p), input),
    adminDeletePool: (p) => request("DELETE", pool(p)),
    adminTestPool: (input) => request("POST", "/admin/pools/test", input),
    adminSftpHostKey: (host, port) => request("POST", "/admin/pools/sftp-host-key", { host, port }),
    adminTLSCertificate: (url) => request("POST", "/admin/pools/tls-certificate", { url }),
    adminSetPoolReplica: (p, replicaPoolId) => request("PUT", `${pool(p)}/replica`, { replicaPoolId }),
    adminProbePool: (p) => request("POST", `${pool(p)}/probe`),
    adminCreateOrg: (input) => request("POST", "/admin/orgs", input),
    adminUpdateOrg: (o, input) => request("PATCH", adminOrg(o), input),
    adminCloseOrg: (o) => request("DELETE", adminOrg(o)),
    adminReopenOrg: (o) => request("POST", `${adminOrg(o)}/reopen`),
    adminPurgeOrg: (o) => request("POST", `${adminOrg(o)}/purge`),
    adminListOrgPools: (o) => request("GET", `${adminOrg(o)}/pools`),
    adminSetOrgPool: (o, p, input) => request("PUT", `${adminOrg(o)}/pools/${encodeURIComponent(p)}`, input),
    adminRemoveOrgPool: (o, p) => request("DELETE", `${adminOrg(o)}/pools/${encodeURIComponent(p)}`),

    listEnrollmentTokens: (o) => request("GET", `${org(o)}/enrollment-tokens`),
    createEnrollmentToken: (o, input) => request("POST", `${org(o)}/enrollment-tokens`, input),

    listActivity: (o, m) => request("GET", `${org(o)}/activity`, undefined, { machineId: m }),
    listAudit: (o) => request("GET", `${org(o)}/audit`),
    listReportKinds: (o) => request("GET", `${org(o)}/reports`),
    listIssuedReports: (o) => request("GET", `${org(o)}/reports/issued`),
    listAlerts: (o, status) => request("GET", `${org(o)}/alerts`, undefined, { status }),
    listNotificationChannels: (o) => request("GET", `${org(o)}/notification-channels`),
    createNotificationChannel: (o, input) => request("POST", `${org(o)}/notification-channels`, input),
    updateNotificationChannel: (o, id, input) => request("PUT", `${org(o)}/notification-channels/${encodeURIComponent(id)}`, input),
    deleteNotificationChannel: (o, id) => request("DELETE", `${org(o)}/notification-channels/${encodeURIComponent(id)}`),
    testNotificationChannel: (o, id) => request("POST", `${org(o)}/notification-channels/${encodeURIComponent(id)}/test`),
    sendMonthlySummary: (o, id) => request("POST", `${org(o)}/notification-channels/${encodeURIComponent(id)}/monthly-summary`),
    listUsers: (o) => request("GET", `${org(o)}/users`),
    updateMember: (o, u, role) => request("PATCH", `${org(o)}/users/${encodeURIComponent(u)}`, { role }),
    removeMember: (o, u) => request("DELETE", `${org(o)}/users/${encodeURIComponent(u)}`),
    resetMemberTotp: async (o, u) => {
      await request<void>("POST", `${org(o)}/users/${encodeURIComponent(u)}/totp-reset`);
    },
    listInvitations: (o) => request("GET", `${org(o)}/invitations`),
    createInvitation: (o, input) => request("POST", `${org(o)}/invitations`, input),
    revokeInvitation: (o, id) => request("DELETE", `${org(o)}/invitations/${encodeURIComponent(id)}`),
    listApiTokens: (o) => request("GET", `${org(o)}/api-tokens`),
    createApiToken: (o, input) => request("POST", `${org(o)}/api-tokens`, input),
    revokeApiToken: (o, id) => request("DELETE", `${org(o)}/api-tokens/${encodeURIComponent(id)}`),
  };
}
